import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import {
  HardHat, Buildings, Briefcase, ShieldCheck, ArrowRight,
  CheckCircle, VideoCamera, Translate, MagnifyingGlass, Sparkle, HandHeart,
} from "@phosphor-icons/react";
import { Layout } from "../components/Layout";

const HERO_IMG = "https://images.unsplash.com/photo-1645597454479-14cb0e8bcabe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBlbGVjdHJpY2lhbiUyMGNhcnBlbnRlciUyMGJsdWUlMjBjb2xsYXIlMjB3b3JrZXJ8ZW58MHx8fHwxNzc4MzgzMzE1fDA&ixlib=rb-4.1.0&q=85";
const PORT_BEAUTY = "https://images.unsplash.com/photo-1723879371824-dfd4bd34d1f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBiZWF1dGljaWFuJTIwc2Fsb24lMjB3b3JrZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzgzODMzMTV8MA&ixlib=rb-4.1.0&q=85";
const PORT_CONTRACT = "https://images.unsplash.com/photo-1697305592193-6a64a63a347e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBlbGVjdHJpY2lhbiUyMGNhcnBlbnRlciUyMGJsdWUlMjBjb2xsYXIlMjB3b3JrZXJ8ZW58MHx8fHwxNzc4MzgzMzE1fDA&ixlib=rb-4.1.0&q=85";

const ROLES = [
  { code: "skilled_individual", icon: HardHat, key_label: "role_individual", key_desc: "role_individual_desc", testid: "role-individual" },
  { code: "msme", icon: Buildings, key_label: "role_msme", key_desc: "role_msme_desc", testid: "role-msme" },
  { code: "employer", icon: Briefcase, key_label: "role_employer", key_desc: "role_employer_desc", testid: "role-employer" },
  { code: "sponsor", icon: HandHeart, key_label: "role_sponsor", key_desc: "role_sponsor_desc", testid: "role-sponsor" },
];

export default function Landing() {
  const { t } = useI18n();
  return (
    <Layout>
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 px-6 md:px-10 pt-10 md:pt-16 pb-12">
          <div className="flex flex-col justify-center">
            <span className="small-label inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[var(--as-accent)] mb-4">
              <Sparkle size={14} weight="fill" /> MYTHIVA<span className="align-super text-[0.7em]">™</span> · Associate Skill · India
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[var(--as-primary)]" style={{fontFamily:"Inter"}} data-testid="hero-title">
              {t("hero_line1")}<br />
              <span className="text-[var(--as-accent)]">{t("hero_line2")}</span><br />
              {t("hero_line3")}
            </h1>
            <p className="mt-6 text-lg text-[var(--as-text-secondary)] max-w-xl leading-relaxed">
              {t("hero_subtitle")}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="btn-primary" data-testid="hero-cta-create">
                {t("cta_create_profile")} <ArrowRight size={20} weight="bold" />
              </Link>
              <Link to="/opportunities" className="btn-secondary" data-testid="hero-cta-jobs">
                <Briefcase size={20} weight="duotone" /> {t("nav_jobs")}
              </Link>
              <Link to="/investors" className="btn-ghost" data-testid="hero-cta-why">
                <Sparkle size={18} weight="duotone" /> Why this matters →
              </Link>
            </div>

            <ul className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { icon: VideoCamera, label: "Video portfolios" },
                { icon: Translate, label: "Multi-language" },
                { icon: ShieldCheck, label: "Verified profiles" },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <li key={i} className="flex flex-col items-start gap-1.5">
                    <Icon size={28} weight="duotone" color="#080F14" />
                    <span className="text-xs font-bold text-[var(--as-primary)]">{item.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="relative grid grid-cols-2 gap-4">
            <div className="col-span-2 relative rounded-2xl overflow-hidden as-card !p-0">
              <img src={HERO_IMG} alt="Skilled worker" className="w-full h-72 object-cover" />
              <div className="absolute inset-0 hero-overlay" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="text-xs font-bold uppercase tracking-widest opacity-90">Featured profile</div>
                <div className="text-xl font-extrabold" style={{fontFamily:"Inter"}}>Ramesh K. · Master Carpenter</div>
                <div className="text-sm opacity-90">Hyderabad, Telangana · 12 yrs</div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden as-card !p-0">
              <img src={PORT_BEAUTY} alt="Beautician" className="w-full h-44 object-cover" />
              <div className="p-3">
                <div className="font-bold text-sm">Sundari Beauty</div>
                <div className="text-xs text-[var(--as-text-secondary)]">Beautician · Bridal makeup</div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden as-card !p-0">
              <img src={PORT_CONTRACT} alt="Contractor" className="w-full h-44 object-cover" />
              <div className="p-3">
                <div className="font-bold text-sm">Anil Contractors</div>
                <div className="text-xs text-[var(--as-text-secondary)]">MSME · Interiors & Fabrication</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROLE CHOICE */}
      <section className="bg-[var(--as-bg-alt)] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)]">{t("get_started")}</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>
                {t("choose_role")}
              </h2>
            </div>
            <Link to="/login" className="btn-ghost" data-testid="already-have-account">
              Already have an account? <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ROLES.map((r) => {
              const Icon = r.icon;
              return (
                <Link key={r.code} to={`/register?role=${r.code}`} className="as-card p-7 group hover:-translate-y-1" data-testid={`landing-${r.testid}`}>
                  <div className="w-14 h-14 rounded-2xl bg-[var(--as-bg-alt)] grid place-items-center mb-5 border border-[var(--as-border)]">
                    <Icon size={32} weight="duotone" color="#080F14" />
                  </div>
                  <div className="text-xl font-bold text-[var(--as-primary)] mb-2" style={{fontFamily:"Inter"}}>
                    {t(r.key_label)}
                  </div>
                  <p className="text-sm text-[var(--as-text-secondary)] leading-relaxed mb-5">
                    {t(r.key_desc)}
                  </p>
                  <span className="inline-flex items-center gap-2 text-sm font-bold text-[var(--as-accent)]">
                    {t("sign_up")} <ArrowRight size={16} weight="bold" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[var(--as-primary)] mb-10" style={{fontFamily:"Inter"}}>
            How it works
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { i: VideoCamera, t: "Show", d: "Upload short videos and photos of your work — in your language." },
              { i: Translate, t: "Translate", d: "Auto transcripts in English, Hindi, Telugu so anyone can understand." },
              { i: ShieldCheck, t: "Verify", d: "Get verified by admin or training partners. Earn trust badges." },
              { i: CheckCircle, t: "Match", d: "Employers find you by skill, location, and experience." },
            ].map((step, idx) => {
              const Icon = step.i;
              return (
                <div key={idx} className="as-card p-6">
                  <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">Step {idx + 1}</div>
                  <Icon size={36} weight="duotone" color="#00B7A6" className="mt-2" />
                  <div className="mt-3 text-lg font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{step.t}</div>
                  <p className="text-sm text-[var(--as-text-secondary)] mt-1 leading-relaxed">{step.d}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-14 as-card p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)]">Are you hiring?</div>
              <h3 className="text-2xl font-bold mt-2 text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>
                Find skilled people in minutes
              </h3>
              <p className="text-[var(--as-text-secondary)] mt-2 max-w-2xl">
                Search by skill and location. See video proof. Hire confidently.
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/register?role=employer" className="btn-primary" data-testid="cta-employer-signup">
                <Briefcase size={20} weight="duotone" /> {t("cta_post_opportunity")}
              </Link>
              <Link to="/search" className="btn-secondary" data-testid="cta-search">
                <MagnifyingGlass size={20} weight="duotone" /> {t("search_talent")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
